import React, { useState, useMemo } from 'react';
import { GraphNode, GraphEdge, GraphData } from './types';
import { useUpdateEdgeMetadata, useUpdateNodeMetadata, useDeleteEdge } from './api';

interface InspectorProps {
  node?: GraphNode | null;
  edge?: GraphEdge | null;
  graphData?: GraphData;
  onClose: () => void;
  canEdit: boolean;
}

export default function Inspector({ node, edge, graphData, onClose, canEdit }: InspectorProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'relationships' | 'metrics' | 'json'>('details');

  const item = node || edge;
  if (!item) return null;

  const isNode = !!node;
  const title = isNode ? node.label : `${edge!.type} connection`;

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'relationships', label: 'Relationships' },
    { id: 'metrics', label: 'Metrics' },
    { id: 'json', label: 'JSON' }
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg truncate" title={title}>
              {title}
            </h2>
            <p className="text-sm text-gray-500">
              {isNode ? node.kind : `${edge!.from} → ${edge!.to}`}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <div className="flex">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'details' && (
          <DetailsTab item={item} isNode={isNode} canEdit={canEdit} />
        )}
        {activeTab === 'relationships' && (
          <RelationshipsTab item={item} isNode={isNode} graphData={graphData} />
        )}
        {activeTab === 'metrics' && (
          <MetricsTab item={item} isNode={isNode} />
        )}
        {activeTab === 'json' && (
          <JsonTab item={item} />
        )}
      </div>
    </div>
  );
}

function DetailsTab({ item, isNode, canEdit }: { item: GraphNode | GraphEdge; isNode: boolean; canEdit: boolean }) {
  const updateNode = useUpdateNodeMetadata();
  const updateEdge = useUpdateEdgeMetadata();
  const deleteEdge = useDeleteEdge();

  if (isNode) {
    const node = item as GraphNode;
    const [label, setLabel] = useState(node.label || '');
    const [tags, setTags] = useState((node.tags || []).join(', '));
    const [propsJson, setPropsJson] = useState(JSON.stringify(node.props || {}, null, 2));
    return (
      <div className="space-y-4">
        <div>
          <h3 className="font-medium text-gray-900 mb-2">Basic Information</h3>
          <dl className="space-y-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">ID</dt>
              <dd className="text-sm text-gray-900 font-mono">{node.id}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Kind</dt>
              <dd className="text-sm text-gray-900">{node.kind}</dd>
            </div>
            {node.props?.iku_category && (
              <div>
                <dt className="text-sm font-medium text-gray-500">IKU Category</dt>
                <dd className="text-sm text-gray-900">{node.props.iku_category}</dd>
              </div>
            )}
            {node.props?.subcategory && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Subcategory</dt>
                <dd className="text-sm text-gray-900">{node.props.subcategory.replace(/_/g, ' ')}</dd>
              </div>
            )}
            {node.props?.attribute_count != null && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Attributes</dt>
                <dd className="text-sm text-gray-900">{node.props.attribute_count}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="text-sm">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  node.status === 'ok' ? 'bg-green-100 text-green-800' :
                  node.status === 'warn' ? 'bg-yellow-100 text-yellow-800' :
                  node.status === 'error' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {node.status}
                </span>
              </dd>
            </div>
          </dl>
        </div>

        {node.tags.length > 0 && (
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Tags</h3>
            <div className="flex flex-wrap gap-1">
              {node.tags.map(tag => (
                <span key={tag} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {canEdit && (
          <div className="pt-4 border-t border-gray-200 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
              <input value={label} onChange={(e) => setLabel(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tags (comma separated)</label>
              <input value={tags} onChange={(e) => setTags(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Props JSON</label>
              <textarea value={propsJson} onChange={(e) => setPropsJson(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" rows={4} />
            </div>
            <button
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-60"
              disabled={updateNode.isPending}
              onClick={async () => {
                try {
                  const parsedProps = JSON.parse(propsJson || '{}');
                  await updateNode.mutateAsync({
                    entity_type: node.props?.entity_type || node.kind,
                    entity_id: node.props?.entity_id || node.id.split(':').slice(1).join(':'),
                    label,
                    tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
                    props: parsedProps,
                  });
                } catch (err: any) {
                  alert(err?.message || 'Invalid node props JSON');
                }
              }}
            >
              {updateNode.isPending ? 'Saving…' : 'Save Node'}
            </button>
          </div>
        )}
      </div>
    );
  } else {
    const edge = item as GraphEdge;
    const [edgePropsJson, setEdgePropsJson] = useState(JSON.stringify(edge.props || {}, null, 2));
    return (
      <div className="space-y-4">
        <div>
          <h3 className="font-medium text-gray-900 mb-2">Connection Details</h3>
          <dl className="space-y-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Type</dt>
              <dd className="text-sm text-gray-900">{edge.type.replace(/_/g, ' ')}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">From</dt>
              <dd className="text-sm text-gray-900 font-mono">{edge.from}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">To</dt>
              <dd className="text-sm text-gray-900 font-mono">{edge.to}</dd>
            </div>
            {edge.props?.category && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Category</dt>
                <dd className="text-sm">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    edge.props.category === 'static' ? 'bg-indigo-100 text-indigo-800' :
                    edge.props.category === 'operational' ? 'bg-amber-100 text-amber-800' :
                    edge.props.category === 'event' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {edge.props.category}
                  </span>
                </dd>
              </div>
            )}
            {edge.props?.cardinality && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Cardinality</dt>
                <dd className="text-sm text-gray-900">{edge.props.cardinality}</dd>
              </div>
            )}
            {edge.props?.description && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="text-sm text-gray-900">{edge.props.description}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="text-sm flex flex-wrap gap-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  edge.status === 'observed' ? 'bg-green-100 text-green-800' :
                  edge.status === 'desired' ? 'bg-orange-100 text-orange-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {edge.status}
                </span>
                {(String(edge.props?.status || '').toLowerCase() === 'superseded' || edge.props?._ui_superseded) && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300">
                    superseded
                  </span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {canEdit && (
          <div className="pt-4 border-t border-gray-200 space-y-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Edge Props JSON</label>
            <textarea value={edgePropsJson} onChange={(e) => setEdgePropsJson(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" rows={4} />
            <button
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-60"
              disabled={updateEdge.isPending}
              onClick={async () => {
                try {
                  const parsed = JSON.parse(edgePropsJson || '{}');
                  await updateEdge.mutateAsync({ edgeId: edge.id, metadata: parsed });
                } catch (err: any) {
                  alert(err?.message || 'Invalid edge props JSON');
                }
              }}
            >
              {updateEdge.isPending ? 'Saving…' : 'Save Connection'}
            </button>
            <button
              className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm disabled:opacity-60"
              disabled={deleteEdge.isPending}
              onClick={async () => { await deleteEdge.mutateAsync(edge.id); }}
            >
              {deleteEdge.isPending ? 'Removing…' : 'Remove Connection'}
            </button>
          </div>
        )}
      </div>
    );
  }
}

const CATEGORY_COLORS: Record<string, string> = {
  static: 'bg-indigo-100 text-indigo-700',
  operational: 'bg-amber-100 text-amber-700',
  event: 'bg-red-100 text-red-700',
};

function RelationshipsTab({ item, isNode, graphData }: { item: GraphNode | GraphEdge; isNode: boolean; graphData?: GraphData }) {
  if (!isNode) {
    return (
      <div className="text-center text-gray-500 py-8">
        Relationships view is only available for nodes
      </div>
    );
  }

  const node = item as GraphNode;

  // 1. Edges from the graph (outgoing and incoming)
  const outgoingEdges = useMemo(() => {
    if (!graphData) return [];
    return graphData.edges.filter(e => e.from === node.id && e.type !== 'belongs_to_iku');
  }, [graphData, node.id]);

  const incomingEdges = useMemo(() => {
    if (!graphData) return [];
    return graphData.edges.filter(e => e.to === node.id && e.type !== 'belongs_to_iku');
  }, [graphData, node.id]);

  // 2. Declared relationships from template props (static/operational/event)
  const declaredRelationships = useMemo(() => {
    const rels: Array<{ type: string; target_iku: string; category: string; cardinality?: string; description?: string }> = [];
    const relationships = node.props?.relationships;
    if (!relationships || typeof relationships !== 'object') return rels;
    for (const cat of ['static', 'operational', 'event']) {
      const list = relationships[cat];
      if (!Array.isArray(list)) continue;
      for (const r of list) {
        rels.push({ ...r, category: cat });
      }
    }
    return rels;
  }, [node.props?.relationships]);

  // Helper to resolve node label from graph
  const resolveLabel = (nodeId: string): string => {
    if (!graphData) return nodeId;
    const n = graphData.nodes.find(x => x.id === nodeId);
    return n?.label || nodeId.replace(/^(iku|template):/, '').replace(/_/g, ' ');
  };

  const hasOutgoing = outgoingEdges.length > 0;
  const hasIncoming = incomingEdges.length > 0;
  const hasDeclared = declaredRelationships.length > 0;
  const hasAny = hasOutgoing || hasIncoming || hasDeclared;

  return (
    <div className="space-y-5">
      {/* Declared relationships from template schema */}
      {hasDeclared && (
        <div>
          <h3 className="font-medium text-gray-900 mb-2 text-sm">Declared Relationships</h3>
          <p className="text-[11px] text-gray-400 mb-2">Defined in template schema — these are the connections this template declares.</p>
          <div className="space-y-2">
            {declaredRelationships.map((rel, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-2.5">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[rel.category] || 'bg-gray-100 text-gray-700'}`}>
                    {rel.category}
                  </span>
                  <span className="text-xs font-medium text-gray-800">
                    {(rel.type || '').replace('rel:', '').replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500">
                  → <span className="font-medium text-gray-700">{(rel.target_iku || '').replace(/_/g, ' ')}</span>
                  {rel.cardinality && <span className="ml-2 text-gray-400">({rel.cardinality})</span>}
                </div>
                {rel.description && (
                  <div className="text-[11px] text-gray-400 mt-1">{rel.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Graph edges: outgoing */}
      {hasOutgoing && (
        <div>
          <h3 className="font-medium text-gray-900 mb-2 text-sm">Connected To ({outgoingEdges.length})</h3>
          <div className="space-y-1.5">
            {outgoingEdges.map(e => (
              <div key={e.id} className="flex items-center gap-2 text-xs p-2 bg-gray-50 rounded-lg">
                <span className="text-gray-400">→</span>
                <span className="font-medium text-gray-700">{resolveLabel(e.to)}</span>
                <span className="text-gray-400 ml-auto">{(e.type || '').replace('rel:', '').replace(/_/g, ' ')}</span>
                {e.props?.category && (
                  <span className={`text-[9px] px-1 py-0.5 rounded ${CATEGORY_COLORS[e.props.category] || 'bg-gray-100 text-gray-600'}`}>
                    {e.props.category}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Graph edges: incoming */}
      {hasIncoming && (
        <div>
          <h3 className="font-medium text-gray-900 mb-2 text-sm">Connected From ({incomingEdges.length})</h3>
          <div className="space-y-1.5">
            {incomingEdges.map(e => (
              <div key={e.id} className="flex items-center gap-2 text-xs p-2 bg-gray-50 rounded-lg">
                <span className="text-gray-400">←</span>
                <span className="font-medium text-gray-700">{resolveLabel(e.from)}</span>
                <span className="text-gray-400 ml-auto">{(e.type || '').replace('rel:', '').replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasAny && (
        <div className="text-center py-8">
          <p className="text-sm text-gray-400">No relationships declared</p>
          <p className="text-xs text-gray-300 mt-1">This template has no declared static, operational, or event relationships</p>
        </div>
      )}
    </div>
  );
}

function MetricsTab({ item, isNode }: { item: GraphNode | GraphEdge; isNode: boolean }) {
  if (!isNode) {
    return <div className="text-center text-gray-500 py-8">Metrics are only available for nodes</div>;
  }

  const node = item as GraphNode;
  const hasMetrics = node.metrics && Object.keys(node.metrics).length > 0;

  if (!hasMetrics) {
    return <div className="text-center text-gray-500 py-8">No metrics available for this node</div>;
  }

  return (
    <div className="space-y-4">
      <h3 className="font-medium text-gray-900 mb-2">Performance Metrics</h3>
      <dl className="space-y-3">
        {Object.entries(node.metrics!).map(([key, value]) => (
          <div key={key} className="flex justify-between">
            <dt className="text-sm font-medium text-gray-500 capitalize">
              {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
            </dt>
            <dd className="text-sm text-gray-900 font-mono">
              {typeof value === 'string' && value.includes('T')
                ? new Date(value).toLocaleString()
                : String(value)
              }
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function JsonTab({ item }: { item: GraphNode | GraphEdge }) {
  return (
    <div>
      <h3 className="font-medium text-gray-900 mb-2">Raw Data</h3>
      <pre className="bg-gray-50 p-3 rounded-md text-xs overflow-x-auto">
        {JSON.stringify(item, null, 2)}
      </pre>
    </div>
  );
}
